import { ApolloClient, InMemoryCache, ApolloProvider, from, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { API_URL_GRAPHQL } from '@common/config/environments';
import React from 'react';

const httpLink = new HttpLink({
  uri: `${API_URL_GRAPHQL}/graphql`,
});

const authLink = setContext(async (_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      ...(token
        ? {
            Authorization: `Bearer ${token}`,
            'Apollo-Require-Preflight': 'true',
          }
        : {}),
    },
  };
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Check if this is a login mutation - we don't want to redirect on login errors
  // The login mutation is named "login" and we check operationName and the error path
  const isLoginOperation = operation?.operationName === 'login' || 
                          (graphQLErrors && graphQLErrors.some(err => 
                            err.path && Array.isArray(err.path) && err.path.includes('login')
                          ));

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );

      // Handle specific error types
      // Don't redirect if this is a login operation - let the component handle the error
      // Check if path array includes 'login' or if operation name is 'login'
      const isLoginError = path && Array.isArray(path) && path.includes('login');
      if (extensions?.code === 'UNAUTHENTICATED' && !isLoginOperation && !isLoginError) {
        // Handle unauthenticated errors (but not login attempts)
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    
    // Handle network errors
    // Don't redirect if this is a login operation - let the component handle the error
    if ('statusCode' in networkError && networkError.statusCode === 401 && !isLoginOperation) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  }
});

// Retry link for network errors
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error) => {
      // Retry on network errors, but not on GraphQL errors
      return !!error && !error.graphQLErrors;
    },
  },
});

// Cache configuration
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        GetUserLoggin: {
          read(existing) {
            return existing;
          },
        },
        GetOwnershipsByCreator: {
          merge(existing = [], incoming) {
            return incoming;
          },
        },
      },
    },
    Ownership: {
      fields: {
        loans: {
          merge(existing = [], incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, retryLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Keep client for backward compatibility with existing code
const client = apolloClient;

interface ApolloAppProviderProps {
  children: React.ReactNode;
}

export const ApolloAppProvider: React.FC<ApolloAppProviderProps> = ({ children }) => {
  return (
    <ApolloProvider client={apolloClient}>
      {children}
    </ApolloProvider>
  );
}; 